import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  FileText,
  File,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  BookMarked,
  Type,
} from "lucide-react";
import { useSources } from "../context/SourcesContext";
import styles from "../styles/sourcesPanel.module.css";

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SourceIcon = ({ type, name }) => {
  const lower = (name || "").toLowerCase();
  if (
    type === "application/pdf" ||
    lower.endsWith(".pdf")
  )
    return <FileText size={14} />;
  if (
    type?.includes("officedocument") ||
    lower.endsWith(".docx") ||
    lower.endsWith(".doc")
  )
    return <File size={14} />;
  return <BookMarked size={14} />;
};

const AddTextModal = ({ onClose, onAdd }) => {
  const [name, setName] = useState("Pasted Text");
  const [text, setText] = useState("");

  return (
    <div className={styles.textModalOverlay} onClick={onClose}>
      <motion.div
        className={styles.textModal}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.textModalHeader}>
          <span>Add Text Source</span>
          <button onClick={onClose} className={styles.textModalClose}>
            ×
          </button>
        </div>
        <input
          className={styles.textModalName}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Source name..."
        />
        <textarea
          className={styles.textModalArea}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste your text, notes, or any content here..."
          autoFocus
        />
        <div className={styles.textModalFooter}>
          <button className={styles.textModalCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.textModalAdd}
            onClick={() => {
              if (text.trim()) {
                onAdd(text.trim(), name.trim() || "Pasted Text");
                onClose();
              }
            }}
            disabled={!text.trim()}
          >
            Add Source
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const SourcesPanel = ({ collapsed, onToggleCollapse }) => {
  const { sources, addFileSource, addTextSource, removeSource } = useSources();
  const fileInputRef = useRef(null);
  const [showTextModal, setShowTextModal] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const handleFiles = async (files) => {
    for (const file of Array.from(files)) {
      await addFileSource(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const readySources = sources.filter((s) => !s.isProcessing && !s.processError);
  const processingSources = sources.filter((s) => s.isProcessing);
  const errorSources = sources.filter((s) => s.processError);

  if (collapsed) {
    return (
      <aside className={`${styles.panel} ${styles.panelCollapsed}`}>
        <button
          className={styles.collapseToggle}
          onClick={onToggleCollapse}
          title="Expand Sources"
        >
          <ChevronRight size={14} />
        </button>
        <div className={styles.collapsedLabel}>
          <span>Sources</span>
          {sources.length > 0 && (
            <span className={styles.collapsedBadge}>{sources.length}</span>
          )}
        </div>
      </aside>
    );
  }

  return (
    <>
      <aside
        className={styles.panel}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerTitle}>Sources</span>
            {sources.length > 0 && (
              <span className={styles.sourceBadge}>{sources.length}</span>
            )}
          </div>
          <div className={styles.headerRight}>
            <div className={styles.addMenuWrapper}>
              <button
                className={styles.addBtn}
                onClick={() => setShowAddMenu((v) => !v)}
                title="Add source"
              >
                <Plus size={13} strokeWidth={2.2} />
                <span>Add</span>
              </button>
              <AnimatePresence>
                {showAddMenu && (
                  <motion.div
                    className={styles.addMenu}
                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                    transition={{ duration: 0.13 }}
                  >
                    <button
                      className={styles.addMenuItem}
                      onClick={() => {
                        fileInputRef.current?.click();
                        setShowAddMenu(false);
                      }}
                    >
                      <FileText size={13} />
                      <span>Upload file</span>
                      <span className={styles.addMenuHint}>PDF, DOCX, TXT</span>
                    </button>
                    <button
                      className={styles.addMenuItem}
                      onClick={() => {
                        setShowTextModal(true);
                        setShowAddMenu(false);
                      }}
                    >
                      <Type size={13} />
                      <span>Paste text</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button
              className={styles.collapseToggle}
              onClick={onToggleCollapse}
              title="Collapse Sources"
            >
              <ChevronLeft size={14} />
            </button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.md"
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />

        {/* Source list */}
        <div className={styles.sourceList}>
          {sources.length === 0 && (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <BookMarked size={24} strokeWidth={1.4} />
              </div>
              <p className={styles.emptyTitle}>No sources yet</p>
              <p className={styles.emptyDesc}>
                Add PDFs, docs, or text to ground Lumi's answers in your
                materials.
              </p>
              <button
                className={styles.emptyAddBtn}
                onClick={() => fileInputRef.current?.click()}
              >
                <Plus size={13} />
                Upload a source
              </button>
            </div>
          )}

          {/* Processing */}
          {processingSources.map((s) => (
            <div key={s.id} className={`${styles.sourceItem} ${styles.sourceProcessing}`}>
              <Loader2 size={13} className={styles.spinner} />
              <span className={styles.sourceName}>{s.name}</span>
              <span className={styles.sourceStatus}>Processing…</span>
            </div>
          ))}

          {/* Ready */}
          {readySources.map((s) => (
            <motion.div
              key={s.id}
              className={`${styles.sourceItem} ${styles.sourceReady}`}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              onMouseEnter={() => setHoveredId(s.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <span className={styles.sourceIconWrapper}>
                <SourceIcon type={s.type} name={s.name} />
              </span>
              <div className={styles.sourceInfo}>
                <span className={styles.sourceName} title={s.name}>
                  {s.name.length > 32 ? s.name.slice(0, 30) + "…" : s.name}
                </span>
                {s.size > 0 && (
                  <span className={styles.sourceMeta}>{formatSize(s.size)}</span>
                )}
              </div>
              <div className={styles.sourceActions}>
                <CheckCircle2 size={12} className={styles.checkIcon} />
                {hoveredId === s.id && (
                  <button
                    className={styles.deleteBtn}
                    onClick={() => removeSource(s.id)}
                    title="Remove source"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </motion.div>
          ))}

          {/* Error */}
          {errorSources.map((s) => (
            <div key={s.id} className={`${styles.sourceItem} ${styles.sourceError}`}>
              <AlertCircle size={13} className={styles.errorIcon} />
              <div className={styles.sourceInfo}>
                <span className={styles.sourceName}>{s.name}</span>
                <span className={styles.sourceErrorMsg}>{s.processError}</span>
              </div>
              <button
                className={styles.deleteBtn}
                onClick={() => removeSource(s.id)}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>

        {/* Drop hint at bottom */}
        <div className={styles.dropHint}>
          <span>Drop files here</span>
        </div>
      </aside>

      <AnimatePresence>
        {showTextModal && (
          <AddTextModal
            onClose={() => setShowTextModal(false)}
            onAdd={(text, name) => addTextSource(text, name)}
          />
        )}
      </AnimatePresence>

      {/* Click outside to close add menu */}
      {showAddMenu && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 98,
          }}
          onClick={() => setShowAddMenu(false)}
        />
      )}
    </>
  );
};

export default SourcesPanel;
